/**
 * AppErrorBoundary — Iter-144 · FIX 3.
 *
 * Renders a visible fallback UI when any descendant throws during
 * render / lifecycle. Prevents the dreaded "blank white screen" and
 * gives the user a clear way to recover (retry / return home) plus
 * the error details for the DevTools console.
 *
 * Kept intentionally simple — no external error reporter dependency
 * so it works offline (matches the PWA offline-first goal).
 */
import React from 'react';

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface the failure so developers can see it in the console even
    // if the fallback UI covers the runtime error text.
    // eslint-disable-next-line no-console
    console.error('[AppErrorBoundary] Uncaught error:', error, info);
  }

  handleRetry = () => this.setState({ error: null });

  handleGoHome = () => { window.location.href = '/'; };

  render() {
    if (!this.state.error) return this.props.children;
    const msg = this.state.error?.message || String(this.state.error);
    return (
      <div
        className="min-h-screen bg-zinc-950 text-white flex items-center justify-center p-6"
        data-testid="app-error-boundary"
      >
        <div className="max-w-md w-full rounded-xl border-2 p-6"
             style={{ borderColor: 'rgba(201,169,98,0.4)', background: 'rgba(255,255,255,0.02)' }}>
          <p className="text-[10px] uppercase tracking-widest font-black mb-2" style={{ color: '#C9A962' }}>
            Something went wrong
          </p>
          <h1 className="text-2xl font-black mb-3">We hit an unexpected snag.</h1>
          <p className="text-sm text-zinc-400 mb-4">
            The screen crashed while rendering. Try again — if the issue continues, tap "Go home" and reopen from the dashboard.
          </p>
          <pre className="text-[10px] text-red-300 bg-black/40 rounded p-2 max-h-32 overflow-auto whitespace-pre-wrap"
               data-testid="app-error-boundary-message">
            {msg}
          </pre>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={this.handleRetry}
              className="flex-1 min-h-[48px] rounded font-black uppercase tracking-widest text-sm"
              style={{ background: '#C9A962', color: '#0a0a0a' }}
              data-testid="app-error-boundary-retry"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={this.handleGoHome}
              className="flex-1 min-h-[48px] rounded font-black uppercase tracking-widest text-sm border border-zinc-600 text-zinc-200 hover:text-white"
              data-testid="app-error-boundary-home"
            >
              Go home
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
